/**
 * Where a floating explanation card goes relative to the thing it explains. Shared by the tour's
 * spotlight card and the inspector's pinned card — they point at the same kind of target and must
 * not disagree about it.
 */

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const rectOf = (el: HTMLElement): Rect => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/** Breathing room between the card and the control it points at. */
const GAP = 12;
/** How close the card may come to the edge of the window. */
const EDGE = 12;

export interface CardSize {
  width: number;
  /**
   * The card's own height is not known until it has rendered, so this is the space to reserve
   * when deciding above-or-below. A generous estimate is the safer error.
   */
  height: number;
}

/**
 * Below the target, or above it when there is no room, clamped so the card stays fully on
 * screen even when the target sits in a corner.
 */
export const cardPosition = (rect: Rect, size: CardSize): { top: number; left: number } => {
  const belowTop = rect.top + rect.height + GAP;
  const fitsBelow = belowTop + size.height < window.innerHeight;
  const top = fitsBelow ? belowTop : Math.max(EDGE, rect.top - size.height - GAP);
  const left = Math.min(
    Math.max(EDGE, rect.left + rect.width / 2 - size.width / 2),
    Math.max(EDGE, window.innerWidth - size.width - EDGE)
  );
  return { top, left };
};
